/**
 * Cron Jobs Page
 * 
 * หน้าจัดการงาน Cron Jobs
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  RefreshCcw,
  Plus,
  Edit,
  Trash2,
  Clock,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Loader2,
  Timer,
  Calendar,
  Percent,
  FileText,
  Bell,
  Database,
  Settings,
  LayoutGrid,
  List,
  ArrowDownCircle,
  ArrowUpCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useAuth } from '@/components/auth/auth-provider'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  CronJobService,
  type CronJob,
  type CronJobFormData,
  type CronJobStats,
  CronJobType,
  CronJobStatus,
  CRON_JOB_TYPES,
  CRON_SCHEDULES,
} from '@/domain/cron-job'
import moment from 'moment'

// ============================================
// Helper Functions
// ============================================

function formatDate(date?: string): string {
  if (!date) return '-'
  return moment(date).format('DD/MM/YYYY HH:mm:ss')
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '-'
  if (seconds < 60) return `${seconds} วินาที`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins} นาที ${secs} วินาที`
}

function getTypeIcon(type: CronJobType, className: string = "w-4 h-4") {
  switch (type) {
    case CronJobType.LOSSBACK: return <Percent className={className} />
    case CronJobType.REPORT: return <FileText className={className} />
    case CronJobType.NOTIFICATION: return <Bell className={className} />
    case CronJobType.CLEANUP: return <Trash2 className={className} />
    case CronJobType.BACKUP: return <Database className={className} />
    case CronJobType.SYNC: return <RefreshCcw className={className} />
    case CronJobType.DEPOSIT: return <ArrowDownCircle className={className} />
    case CronJobType.WITHDRAW: return <ArrowUpCircle className={className} />
    case CronJobType.WINLOSE: return <Percent className={className} />
    case CronJobType.ACHIEVEMENT: return <CheckCircle className={className} />
    case CronJobType.COMMISSION: return <Percent className={className} />
    default: return <Settings className={className} />
  }
}

function getStatusBadge(status?: CronJobStatus) {
  switch (status) {
    case CronJobStatus.SUCCESS:
      return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1">
        <CheckCircle className="w-3 h-3" /> สำเร็จ
      </Badge>
    case CronJobStatus.FAILED:
      return <Badge className="bg-red-500/15 text-red-600 border-red-500/30 gap-1">
        <XCircle className="w-3 h-3" /> ล้มเหลว
      </Badge>
    case CronJobStatus.RUNNING:
      return <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30 gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> กำลังรัน
      </Badge>
    default:
      return <Badge className="bg-zinc-500/15 text-zinc-600 border-zinc-500/30 gap-1">
        <Clock className="w-3 h-3" /> รอดำเนินการ
      </Badge>
  }
}

// ============================================
// Stats Cards
// ============================================

// ============================================
// Job Card Component
// ============================================

function CronJobCard({
  job,
  onEdit,
  onDelete,
  onToggle,
  onRun,
  isToggling,
  isRunning,
}: {
  job: CronJob
  onEdit: () => void
  onDelete: () => void
  onToggle: () => void
  onRun: () => void
  isToggling: boolean
  isRunning: boolean
}) {
  const typeInfo = CRON_JOB_TYPES.find(t => t.value === job.type)
  const scheduleLabel = CRON_SCHEDULES.find(s => s.value === job.schedule)?.label

  // Color mapping for types
  const typeColors: Record<CronJobType, { bg: string; text: string; border: string; bar: string }> = {
    [CronJobType.LOSSBACK]: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/30', bar: 'bg-red-500' },
    [CronJobType.REPORT]: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/30', bar: 'bg-blue-500' },
    [CronJobType.NOTIFICATION]: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/30', bar: 'bg-amber-500' },
    [CronJobType.CLEANUP]: { bg: 'bg-zinc-500/10', text: 'text-zinc-600', border: 'border-zinc-500/30', bar: 'bg-zinc-500' },
    [CronJobType.BACKUP]: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/30', bar: 'bg-emerald-500' },
    [CronJobType.SYNC]: { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500/30', bar: 'bg-purple-500' },
    [CronJobType.DEPOSIT]: { bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/30', bar: 'bg-green-500' },
    [CronJobType.WITHDRAW]: { bg: 'bg-orange-500/10', text: 'text-orange-600', border: 'border-orange-500/30', bar: 'bg-orange-500' },
    [CronJobType.WINLOSE]: { bg: 'bg-pink-500/10', text: 'text-pink-600', border: 'border-pink-500/30', bar: 'bg-pink-500' },
    [CronJobType.ACHIEVEMENT]: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', border: 'border-yellow-500/30', bar: 'bg-yellow-500' },
    [CronJobType.COMMISSION]: { bg: 'bg-indigo-500/10', text: 'text-indigo-600', border: 'border-indigo-500/30', bar: 'bg-indigo-500' },
  }

  const colors = typeColors[job.type] || typeColors[CronJobType.LOSSBACK]

  return (
    <Card className={cn(
      "border-zinc-200/50 dark:border-zinc-800 overflow-hidden transition-all hover:shadow-md",
      !job.is_active && "opacity-60"
    )}>
      {/* Header with type color */}
      <div className={cn("h-1.5", colors.bar)} />
      
      <CardContent className="p-4">
        {/* Title & Actions */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", colors.bg)}>
              <span className={colors.text}>
                {getTypeIcon(job.type, "w-5 h-5")}
              </span>
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-sm truncate">{job.name}</h3>
              <Badge variant="outline" className={cn("text-xs gap-1 mt-1", colors.border, colors.text)}>
                {typeInfo?.label || job.type}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7 text-red-500 hover:text-red-600" 
              onClick={onDelete}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Description */}
        {job.description && (
          <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
            {job.description}
          </p>
        )}

        {/* Run Now / Retry Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onRun}
          disabled={isRunning}
          className={cn(
            "w-full mb-3",
            colors.border,
            colors.text,
            "hover:bg-opacity-10",
            isRunning && "opacity-50 cursor-not-allowed"
          )}
        >
          {isRunning ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              กำลังรัน...
            </>
          ) : job.last_status === CronJobStatus.FAILED ? (
            <>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Retry
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Run Now
            </>
          )}
        </Button>

        {/* Schedule */}
        <div className="flex items-center gap-2 mb-3 p-2 bg-muted/50 rounded-lg">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <div className="text-xs">
            <code className="bg-background px-1.5 py-0.5 rounded font-mono">
              {job.schedule}
            </code>
            {scheduleLabel && (
              <span className="text-muted-foreground ml-2">({scheduleLabel})</span>
            )}
          </div>
        </div>

        {/* Status & Last Run */}
        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
          <div className="p-2 bg-muted/30 rounded">
            <p className="text-muted-foreground mb-1">สถานะล่าสุด</p>
            {getStatusBadge(job.last_status)}
          </div>
          <div className="p-2 bg-muted/30 rounded">
            <p className="text-muted-foreground mb-1">รันล่าสุด</p>
            <p className="font-medium">
              {job.last_run_at ? formatDate(job.last_run_at) : '-'}
            </p>
          </div>
        </div>

        {/* Error */}
        {job.last_error && (
          <div className="p-2 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800 mb-3">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
              ⚠️ {job.last_error}
            </p>
          </div>
        )}

        {/* Footer: Toggle & Run Count */}
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-2">
            <Switch
              checked={job.is_active}
              onCheckedChange={onToggle}
              disabled={isToggling}
            />
            <span className="text-xs text-muted-foreground">
              {job.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
            </span>
          </div>
          <Badge variant="secondary" className="text-xs">
            รัน {job.run_count} ครั้ง
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Stats Cards
// ============================================

function StatsCards({ stats, isLoading }: { stats: CronJobStats | null; isLoading: boolean }) {
  const cards = [
    { label: 'งานทั้งหมด', value: stats?.total || 0, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-500/10' },
    { label: 'เปิดใช้งาน', value: stats?.active || 0, icon: Play, color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
    { label: 'ปิดใช้งาน', value: stats?.inactive || 0, icon: Pause, color: 'text-zinc-500', bg: 'bg-zinc-500/10' },
    { label: 'ล้มเหลว', value: stats?.failed || 0, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-500/10' },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.label} className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2.5 rounded-xl", card.bg)}>
                <card.icon className={cn("w-5 h-5", card.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{card.label}</p>
                {isLoading ? (
                  <Skeleton className="h-6 w-12 mt-1" />
                ) : (
                  <p className="text-xl font-bold mt-0.5">{card.value}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================
// Create/Edit Dialog
// ============================================

function CronJobDialog({
  open,
  onClose,
  onSuccess,
  job,
  isEdit,
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  job?: CronJob | null
  isEdit: boolean
}) {
  const { sessionToken } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<CronJobType>(CronJobType.LOSSBACK)
  const [schedule, setSchedule] = useState('0 2 * * *')
  const [customSchedule, setCustomSchedule] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [config, setConfig] = useState<string>('{}')

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (isEdit && job) {
        setName(job.name)
        setDescription(job.description)
        setType(job.type)
        setSchedule(job.schedule)
        setCustomSchedule('')
        setIsActive(job.is_active)
        setConfig(JSON.stringify(job.config, null, 2))
      } else {
        setName('')
        setDescription('')
        setType(CronJobType.LOSSBACK)
        setSchedule('0 2 * * *')
        setCustomSchedule('')
        setIsActive(true)
        setConfig('{}')
      }
      setNameError(null)
    }
  }, [open, isEdit, job])

  const handleNameCheck = async () => {
    if (!sessionToken || !name) return
    const exists = await CronJobService.checkNameExists(sessionToken, name, isEdit ? job?.objectId : undefined)
    setNameError(exists ? 'ชื่อนี้ถูกใช้แล้ว' : null)
  }

  const handleSubmit = async () => {
    if (!sessionToken || !name) return
    if (nameError) return

    setIsSubmitting(true)
    try {
      let parsedConfig = {}
      try {
        parsedConfig = JSON.parse(config)
      } catch {
        parsedConfig = {}
      }

      const data: CronJobFormData = {
        name,
        description,
        type,
        schedule: customSchedule || schedule,
        is_active: isActive,
        config: parsedConfig,
      }

      if (isEdit && job) {
        await CronJobService.update(sessionToken, job.objectId, data)
      } else {
        await CronJobService.create(sessionToken, data)
      }

      onSuccess()
      onClose()
    } catch (err: any) {
      toast.error(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isCustomSchedule = !CRON_SCHEDULES.some(s => s.value === schedule)

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            {isEdit ? 'แก้ไข Cron Job' : 'สร้าง Cron Job ใหม่'}
          </DialogTitle>
          <DialogDescription>
            กำหนดงานที่ต้องการให้รันอัตโนมัติตามเวลาที่กำหนด
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label>ชื่องาน <span className="text-red-500">*</span></Label>
            <Input
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(null) }}
              onBlur={handleNameCheck}
              placeholder="เช่น daily_lossback, weekly_report"
              className={cn(nameError && "border-red-500")}
            />
            {nameError && <p className="text-xs text-red-500">{nameError}</p>}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>คำอธิบาย</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="อธิบายว่างานนี้ทำอะไร..."
              rows={2}
            />
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label>ประเภทงาน</Label>
            <Select value={type} onValueChange={(v) => setType(v as CronJobType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRON_JOB_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="flex items-center gap-2">
                      {getTypeIcon(t.value)}
                      {t.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Schedule */}
          <div className="space-y-2">
            <Label>กำหนดเวลา (Cron Schedule)</Label>
            <Select value={isCustomSchedule ? 'custom' : schedule} onValueChange={(v) => {
              if (v === 'custom') {
                setCustomSchedule(schedule)
              } else {
                setSchedule(v)
                setCustomSchedule('')
              }
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRON_SCHEDULES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
                <SelectItem value="custom">กำหนดเอง...</SelectItem>
              </SelectContent>
            </Select>
            {(isCustomSchedule || customSchedule) && (
              <Input
                value={customSchedule || schedule}
                onChange={(e) => setCustomSchedule(e.target.value)}
                placeholder="*/5 * * * *"
                className="font-mono"
              />
            )}
            <p className="text-xs text-muted-foreground">
              รูปแบบ: นาที ชั่วโมง วัน เดือน วันในสัปดาห์
            </p>
          </div>

          {/* Config (JSON) */}
          <div className="space-y-2">
            <Label>Config (JSON)</Label>
            <Textarea
              value={config}
              onChange={(e) => setConfig(e.target.value)}
              placeholder='{ "key": "value" }'
              rows={3}
              className="font-mono text-sm"
            />
          </div>

          {/* Active */}
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <span className="text-sm">เปิดใช้งาน</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !name || !!nameError}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'บันทึก' : 'สร้าง'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Main Component
// ============================================

export function CronJobsPage() {
  const { sessionToken } = useAuth()

  // State
  const [jobs, setJobs] = useState<CronJob[]>([])
  const [stats, setStats] = useState<CronJobStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Dialog state
  const [showDialog, setShowDialog] = useState(false)
  const [selectedJob, setSelectedJob] = useState<CronJob | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [jobToDelete, setJobToDelete] = useState<CronJob | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [runningId, setRunningId] = useState<string | null>(null)
  
  // Run Now Confirm Dialog
  const [jobToRun, setJobToRun] = useState<CronJob | null>(null)
  const [showRunConfirm, setShowRunConfirm] = useState(false)

  // Load data
  const loadData = useCallback(async () => {
    if (!sessionToken) return

    setIsLoading(true)
    try {
      const [jobsResult, statsResult] = await Promise.all([
        CronJobService.find(sessionToken, { query: searchQuery || undefined }),
        CronJobService.getStats(sessionToken),
      ])
      setJobs(jobsResult.items)
      setStats(statsResult)
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, searchQuery])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleCreate = () => {
    setSelectedJob(null)
    setIsEditMode(false)
    setShowDialog(true)
  }

  const handleEdit = (job: CronJob) => {
    setSelectedJob(job)
    setIsEditMode(true)
    setShowDialog(true)
  }

  const handleDelete = (job: CronJob) => {
    setJobToDelete(job)
    setShowDeleteDialog(true)
  }

  const confirmDelete = async () => {
    if (!jobToDelete || !sessionToken) return

    setIsDeleting(true)
    try {
      await CronJobService.delete(sessionToken, jobToDelete.objectId)
      await loadData()
    } catch (err: any) {
      toast.error(`ไม่สามารถลบได้: ${err.message}`)
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
      setJobToDelete(null)
    }
  }

  const handleToggleActive = async (job: CronJob) => {
    if (!sessionToken) return

    setTogglingId(job.objectId)
    try {
      await CronJobService.toggleActive(sessionToken, job.objectId, !job.is_active)
      await loadData()
    } catch (err: any) {
      toast.error(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setTogglingId(null)
    }
  }

  // Show confirm dialog before run
  const handleRunNow = (job: CronJob) => {
    setJobToRun(job)
    setShowRunConfirm(true)
  }

  // Execute run job after confirmation
  const executeRunJob = async () => {
    if (!sessionToken || !jobToRun) return

    setRunningId(jobToRun.objectId)
    setShowRunConfirm(false)
    
    try {
      const result = await CronJobService.testCronJob(sessionToken, jobToRun.name)
      if (result.success) {
        // Success - show brief toast or update UI
        console.log('Job executed successfully:', result)
      } else {
        toast.error(result.message || result.error || 'รันงานล้มเหลว')
      }
      await loadData()
    } catch (err: any) {
      toast.error(err.message || 'ไม่สามารถรันงานได้')
    } finally {
      setRunningId(null)
      setJobToRun(null)
    }
  }

  const handleCreateDefaults = async () => {
    if (!sessionToken) return

    setIsCreatingDefaults(true)
    try {
      const result = await CronJobService.createDefaultJobs(sessionToken)
      if (result.created.length > 0) {
        toast.success(`สร้างงานตัวอย่างสำเร็จ ${result.created.length} งาน!`)
      } else {
        toast.info('งานตัวอย่างทั้งหมดมีอยู่แล้ว')
      }
      await loadData()
    } catch (err: any) {
      toast.error(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsCreatingDefaults(false)
    }
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Clock className="w-6 h-6 text-blue-500" />
            Cron Jobs
          </h1>
          <p className="text-sm text-muted-foreground">
            จัดการงานที่รันอัตโนมัติตามกำหนดเวลา
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button 
              variant={viewMode === 'grid' ? 'default' : 'ghost'} 
              size="icon" 
              className="rounded-none h-9 w-9"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'ghost'} 
              size="icon" 
              className="rounded-none h-9 w-9"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="icon" onClick={loadData} disabled={isLoading}>
            <RefreshCcw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button className="gap-2" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">สร้างงานใหม่</span>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <StatsCards stats={stats} isLoading={isLoading} />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="ค้นหางาน..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Jobs Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-zinc-200/50 dark:border-zinc-800">
              <CardContent className="p-4">
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <Card className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-12 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">ยังไม่มี Cron Jobs</p>
            <p className="text-sm text-muted-foreground mb-6">
              เริ่มต้นด้วยการสร้างงานตัวอย่าง หรือสร้างงานใหม่ตามต้องการ
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button 
                onClick={handleCreateDefaults} 
                disabled={isCreatingDefaults}
                className="gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
              >
                {isCreatingDefaults ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Timer className="h-4 w-4" />
                )}
                สร้างงานตัวอย่าง (จาก Backend)
              </Button>
              <Button onClick={handleCreate} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                สร้างงานใหม่
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        // Grid View
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((job) => (
            <CronJobCard
              key={job.objectId}
              job={job}
              onEdit={() => handleEdit(job)}
              onDelete={() => handleDelete(job)}
              onToggle={() => handleToggleActive(job)}
              onRun={() => handleRunNow(job)}
              isToggling={togglingId === job.objectId}
              isRunning={runningId === job.objectId}
            />
          ))}
        </div>
      ) : (
        // List View
        <Card className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ชื่องาน</TableHead>
                  <TableHead>ประเภท</TableHead>
                  <TableHead>กำหนดเวลา</TableHead>
                  <TableHead>สถานะล่าสุด</TableHead>
                  <TableHead>รันล่าสุด</TableHead>
                  <TableHead>เปิด/ปิด</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const typeInfo = CRON_JOB_TYPES.find(t => t.value === job.type)
                  return (
                    <TableRow key={job.objectId} className={cn(!job.is_active && "opacity-60")}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{job.name}</p>
                          {job.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {job.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          {getTypeIcon(job.type)}
                          {typeInfo?.label || job.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {job.schedule}
                        </code>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(job.last_status)}
                        {job.last_error && (
                          <p className="text-xs text-red-500 mt-1 truncate max-w-[150px]" title={job.last_error}>
                            {job.last_error}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <p>{formatDate(job.last_run_at)}</p>
                          {job.last_duration != null && (
                            <p className="text-muted-foreground">
                              ({formatDuration(job.last_duration)})
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={job.is_active}
                          onCheckedChange={() => handleToggleActive(job)}
                          disabled={togglingId === job.objectId}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(job)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(job)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <CronJobDialog
        open={showDialog}
        onClose={() => { setShowDialog(false); setSelectedJob(null) }}
        onSuccess={loadData}
        job={selectedJob}
        isEdit={isEditMode}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบ Cron Job</AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการลบงาน "{jobToDelete?.name}" หรือไม่?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              ลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Run Now Confirm Dialog */}
      <ConfirmDialog
        open={showRunConfirm}
        onOpenChange={setShowRunConfirm}
        title={`ยืนยันรันงาน "${jobToRun?.name || ''}"`}
        description="คุณต้องการรันงานนี้ทันทีหรือไม่?"
        variant="warning"
        confirmLabel="รันเลย"
        details={jobToRun && (
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">ประเภท:</span>
              <span className="font-medium">{jobToRun.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">กำหนดเวลา:</span>
              <span className="font-mono text-xs">{jobToRun.schedule}</span>
            </div>
            <div className="mt-2 text-xs text-amber-600">
              ⚠️ การรันงานอาจใช้เวลาสักครู่และอาจมีผลกระทบต่อระบบ
            </div>
          </div>
        )}
        onConfirm={executeRunJob}
      />
    </div>
  )
}
